import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { toast } from "components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { DataTable } from "../components/data-table";
import { columns } from "../components/columns";
import { SummaryCards } from "../components/summary-cards";
import { FilterBar } from "../components/filter-bar";
import { RefreshButton } from "../components/refresh-button";
import { TransactionDetailContent } from "../components/transaction-detail-modal";
import { SelectionInfo } from "../components/selection-info";
import { ExportModalContent } from "../components/export-modal";
import { ImportButton } from "../components/import-button";
import { ImportResultsContent } from "../components/import-results-modal";
import {
  useGetTransactionsQuery,
  useGetSummaryQuery,
  cavOfflineSyncApi,
} from "@/services/cav-offline-sync";
import {
  ExportTransactionsQuery,
  TransactionListItem,
  ImportResponse,
  SortField,
  SortOrder,
} from "@/services/cav-offline-sync/types";
import { Meta } from "@/@types/paginated";
import { useCavOfflineSyncState } from "../ducks/hooks/state";
import { useCavOfflineSyncFilterActions } from "../ducks/hooks/action";
import { AppDispatch } from "@/store";
import {
  generateCSV,
  generateFilename,
  downloadCSV,
} from "../utils/csv-export";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { MESSAGES_CAV } from "../utils/messages";

export const CavOfflineSyncList: React.FC = () => {
  const { filters } = useCavOfflineSyncState();
  const { updatePagination, updateSort } = useCavOfflineSyncFilterActions();
  const dispatch = useDispatch<AppDispatch>();
  const { handleModal } = useDialogModal();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const prevFiltersRef = useRef(filters);

  const queryParams = {
    page: filters.meta.currentPage,
    limit: filters.meta.perPage,
    status: filters.status.length > 0 ? filters.status : undefined,
    storeIds: filters.storeIds.length > 0 ? filters.storeIds : undefined,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
  };

  const {
    data: response,
    isLoading,
    isFetching: isFetchingList,
    error,
    refetch: refetchList,
  } = useGetTransactionsQuery(queryParams);

  const summaryFilters = {
    status: filters.status.length > 0 ? filters.status : undefined,
    storeIds: filters.storeIds.length > 0 ? filters.storeIds : undefined,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  };

  const { isFetching: isFetchingSummary, refetch: refetchSummary } =
    useGetSummaryQuery(summaryFilters);

  const isRefreshing = isFetchingList || isFetchingSummary;

  const handleRefresh = async () => {
    try {
      const results = await Promise.all([refetchList(), refetchSummary()]);
      const hasError = results.some((result) => result.isError);
      if (hasError) {
        throw new Error("Refresh failed");
      }
    } catch {
      toast(MESSAGES_CAV.REFRESH.ERROR);
    }
  };

  const handleRowClick = (transaction: TransactionListItem) => {
    handleModal({
      isOpen: true,
      element: (
        <TransactionDetailContent
          store={transaction.store}
          docCode={transaction.docCode}
        />
      ),
      title: "Detalhes da Transacao",
    });
  };

  const handlePageChange = (newPage: number) => {
    updatePagination(newPage, filters.meta.perPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    updatePagination(1, newPageSize);
  };

  const handleSortChange = (sortBy: string | undefined, sortOrder: "asc" | "desc" | undefined) => {
    updateSort(sortBy as SortField | undefined, sortOrder as SortOrder | undefined);
  };

  const handleSelectionChange = (ids: Set<string>) => {
    setSelectedIds(ids);
    if (selectAllFiltered && ids.size < (response?.meta?.total || 0)) {
      setSelectAllFiltered(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllFiltered(false);
  };

  const handleSelectAllFiltered = () => {
    setSelectAllFiltered(true);
  };

  const exportAbortControllerRef = useRef<AbortController | null>(null);

  const handleExport = async (exportColumns: string[]) => {
    setIsExporting(true);

    try {
      let dataToExport: TransactionListItem[];

      if (selectAllFiltered) {
        exportAbortControllerRef.current = new AbortController();

        const exportParams: ExportTransactionsQuery = {
          status: filters.status.length > 0 ? filters.status : undefined,
          storeIds: filters.storeIds.length > 0 ? filters.storeIds : undefined,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
        };

        const result = await dispatch(
          cavOfflineSyncApi.endpoints.exportTransactions.initiate(exportParams, {
            forceRefetch: true,
          })
        ).unwrap();

        dataToExport = result;
      } else {
        dataToExport =
          response?.data.filter((row) =>
            selectedIds.has(`${row.store}-${row.docCode}`)
          ) || [];
      }

      const csvContent = generateCSV(dataToExport, exportColumns);
      const filename = generateFilename();
      downloadCSV(csvContent, filename);

      toast(MESSAGES_CAV.EXPORT.SUCCESS(dataToExport.length));
    } catch (err) {
      if ((err as Error).name === "AbortError") {
        return;
      }
      console.error("Export error:", err);
      toast(MESSAGES_CAV.EXPORT.ERROR);
    } finally {
      exportAbortControllerRef.current = null;
      setIsExporting(false);
    }
  };

  const handleExportClick = () => {
    if (selectedIds.size === 0 && !selectAllFiltered) {
      toast(MESSAGES_CAV.EXPORT.EMPTY_SELECTION);
      return;
    }

    handleModal({
      isOpen: true,
      element: (
        <ExportModalContent
          selectedCount={selectedIds.size}
          selectAllFiltered={selectAllFiltered}
          totalFiltered={meta.total}
          onExport={handleExport}
        />
      ),
      title: "Exportar Transacoes",
    });
  };

  const handleImportSuccess = (result: ImportResponse) => {
    handleModal({
      isOpen: true,
      element: <ImportResultsContent result={result} />,
      title: "Resultado da Importacao",
    });
  };

  useEffect(() => {
    return () => {
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
        exportAbortControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current.storeIds !== filters.storeIds ||
      prevFiltersRef.current.status !== filters.status ||
      prevFiltersRef.current.dateFrom !== filters.dateFrom ||
      prevFiltersRef.current.dateTo !== filters.dateTo;

    if (filtersChanged) {
      setSelectedIds(new Set());
      setSelectAllFiltered(false);
    }

    prevFiltersRef.current = filters;
  }, [filters.storeIds, filters.status, filters.dateFrom, filters.dateTo]);

  if (error) {
    toast(MESSAGES_CAV.LIST.ERROR);
  }

  const defaultMeta: Meta = {
    total: 0,
    currentPage: 1,
    perPage: filters.meta.perPage,
    lastPage: 0,
    prev: null,
    next: null,
  };
  const { data = [], meta = defaultMeta } = response || { data: [], meta: defaultMeta };

  return (
    <PageLayout>
      <PageTitle
        title="Sincronização CAV Offline"
        description="Visualize e gerencie transações CAV offline pendentes de sincronização"
      />
      <ContentContainer>
        <div className="flex justify-end gap-2 mb-4">
          <ImportButton onSuccess={handleImportSuccess} />
          <Button
            variant="outline"
            onClick={handleExportClick}
            disabled={isExporting}
          >
            {isExporting ? "Exportando..." : "Exportar"}
          </Button>
          <RefreshButton onClick={handleRefresh} isLoading={isRefreshing} />
        </div>
        <SummaryCards filters={summaryFilters} />
        <FilterBar />
        <SelectionInfo
          selectedCount={selectedIds.size}
          selectAllFiltered={selectAllFiltered}
          totalFiltered={meta.total}
          onClearSelection={handleClearSelection}
          onSelectAllFiltered={handleSelectAllFiltered}
        />
        <DataTable
          columns={columns}
          data={data}
          meta={meta}
          isLoading={isLoading}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSortChange={handleSortChange}
        />
      </ContentContainer>
    </PageLayout>
  );
};

export default CavOfflineSyncList;
